import { Seo } from '../components/Seo';
import Logo from '../components/Logo';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  Cloud, 
  Eye, 
  Database, 
  CreditCard, 
  Trash2, 
  ArrowLeft,
  Lock,
  Server,
  CheckCircle
} from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "24 May 2026";
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Privacy Policy | VerifAEye"
        description="Read how VerifAEye protects your data: photos stay in your own Google Drive, minimal data collection, and Australian privacy compliance for policyholders."
        path="/privacy"
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="sm" />
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-8 text-white">
            <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-slate-300">Last updated: {lastUpdated}</p>
          </div>

          <div className="p-8 space-y-8">
            {/* Key Privacy Highlights */}
            <section className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Key Privacy Highlights
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-green-800">No images stored on our servers</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-green-800">Your photos stay in YOUR Google Drive</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-green-800">AI analysis is transient (not retained)</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-green-800">You can delete your data anytime</span>
                </div>
              </div>
            </section>

            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Introduction</h2>
              <p className="text-slate-600 leading-relaxed">
                VerifAEye ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, and safeguard your information when you use our forensic 
                insurance inventory service. We operate on a <strong>privacy-first, local-first</strong> model, 
                meaning your personal photos and sensitive data remain under your control at all times.
              </p>
            </section>

            {/* Image Storage */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Cloud className="w-5 h-5 text-blue-600" />
                2. Image Storage (BYOS - Bring Your Own Storage)
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  <strong>We do NOT store your original photos on VerifAEye servers.</strong> Our service 
                  operates using a "Bring Your Own Storage" (BYOS) model:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Google Drive Integration:</strong> When you connect your Google Drive, your 
                    high-resolution photos are saved directly to a folder called "Verifaeye_Vault" in 
                    YOUR Google Drive account.
                  </li>
                  <li>
                    <strong>Local Thumbnails:</strong> Small thumbnail images may be cached locally in 
                    your browser's IndexedDB for faster loading of your inventory list.
                  </li>
                  <li>
                    <strong>No Server-Side Storage:</strong> We never upload, store, or retain your 
                    original photos on our infrastructure.
                  </li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="google-limited-use-statement">
                  <p className="text-sm text-blue-900 leading-relaxed">
                    <strong>Google API Services User Data Policy:</strong> VerifAEye's use and transfer to any
                    other app of information received from Google APIs will adhere to the{' '}
                    <a
                      href="https://developers.google.com/terms/api-services-user-data-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-blue-700"
                    >
                      Google API Services User Data Policy
                    </a>
                    , including the Limited Use requirements.
                  </p>
                </div>
              </div>
            </section>

            {/* AI Analysis */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-600" />
                3. AI Analysis & Transient Processing
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  Our AI-powered item recognition uses Google's Gemini API for analysis:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Transient Analysis:</strong> Images are sent to the AI service for analysis 
                    and are NOT retained after processing. The analysis is performed in real-time and 
                    the image data is immediately discarded.
                  </li>
                  <li>
                    <strong>No AI Training:</strong> Your images are not used to train any AI models. 
                    We use the Gemini API with data logging disabled.
                  </li>
                  <li>
                    <strong>Analysis Results:</strong> Only the text-based results (item names, estimated 
                    values, categories) are stored in your account—never the actual images.
                  </li>
                </ul>
              </div>
            </section>

            {/* Data We Store */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Database className="w-5 h-5 text-orange-600" />
                4. Data We DO Store
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  The following information IS stored on our secure servers:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Account Information:</strong> Email address, name, and encrypted password 
                    (hashed using industry-standard bcrypt).
                  </li>
                  <li>
                    <strong>Inventory Metadata:</strong> Item names, descriptions, categories, rooms, 
                    estimated values, and analysis results.
                  </li>
                  <li>
                    <strong>Google Drive References:</strong> File IDs and paths pointing to your photos 
                    in your own Google Drive (not the photos themselves).
                  </li>
                  <li>
                    <strong>Usage Data:</strong> Analysis count, purchase history, and feature unlock status.
                  </li>
                  <li>
                    <strong>Notification Preferences:</strong> Your chosen notification settings.
                  </li>
                </ul>
              </div>
            </section>

            {/* Payment Data */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                5. Payment Information
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  We use <strong>Stripe</strong> to process all payments:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>No Card Storage:</strong> We never see, store, or have access to your full 
                    credit card number. All payment processing is handled directly by Stripe.
                  </li>
                  <li>
                    <strong>Purchase Records:</strong> We store a record of your purchases (date, amount, 
                    credits purchased) but NOT your payment method details.
                  </li>
                  <li>
                    <strong>PCI Compliance:</strong> Stripe is PCI DSS Level 1 certified, the highest 
                    level of payment security certification.
                  </li>
                </ul>
                <p className="text-sm">
                  For more information, see{' '}
                  <a 
                    href="https://stripe.com/au/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Stripe's Privacy Policy
                  </a>.
                </p>
              </div>
            </section>

            {/* Data Deletion */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-600" />
                6. Data Deletion & Your Rights
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  You have full control over your data:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Delete Individual Items:</strong> Remove any inventory item at any time 
                    from your dashboard.
                  </li>
                  <li>
                    <strong>Delete Account:</strong> Request complete account deletion by contacting 
                    us at privacy@verifaeye.com.au. This will remove all your inventory data from 
                    our servers within 30 days.
                  </li>
                  <li>
                    <strong>Google Drive Photos:</strong> Photos in your Verifaeye_Vault folder remain 
                    in YOUR Google Drive and are not affected by account deletion. You manage these 
                    directly in Google Drive.
                  </li>
                  <li>
                    <strong>Data Export:</strong> Export your full inventory as CSV at any time from 
                    the Inventory page.
                  </li>
                </ul>
              </div>
            </section>

            {/* Security */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5 text-slate-600" />
                7. Security Measures
              </h2>
              <div className="space-y-4 text-slate-600">
                <ul className="list-disc pl-6 space-y-2">
                  <li>All data transmitted via HTTPS/TLS encryption</li>
                  <li>Passwords hashed using bcrypt with salt</li>
                  <li>JWT tokens for secure authentication with expiration</li>
                  <li>Google OAuth 2.0 with PKCE for secure Drive authorization</li>
                  <li>Regular security audits and updates</li>
                </ul>
              </div>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Server className="w-5 h-5 text-slate-600" />
                8. Third-Party Services
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">We integrate with the following third-party services:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Google Drive API:</strong> For BYOS photo storage (with your authorization)</li>
                  <li><strong>Google Gemini API:</strong> For AI-powered item analysis (transient processing)</li>
                  <li><strong>Stripe:</strong> For secure payment processing</li>
                  <li><strong>Resend:</strong> For transactional emails (password reset, verification)</li>
                </ul>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-slate-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Contact Us</h2>
              <p className="text-slate-600">
                If you have questions about this Privacy Policy or your data, please reach out:
              </p>
              <div className="mt-3 grid gap-4 sm:grid-cols-[1fr_auto] items-start">
                <div className="space-y-1.5 text-sm text-slate-700">
                  <p>
                    Email:{' '}
                    <a href="mailto:brucev@symbionic.com.au" className="text-blue-600 hover:underline font-medium" data-testid="contact-email-privacy">
                      brucev@symbionic.com.au
                    </a>
                  </p>
                  <p>
                    WhatsApp:{' '}
                    <a href="https://wa.me/qr" className="text-blue-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">
                      scan the QR
                    </a>
                    <span className="text-slate-500"> — opens a chat with Bruce.</span>
                  </p>
                  <p className="pt-2 text-slate-600">
                    For more insurance information, tips, and definitions — free of charge — visit{' '}
                    <a href="https://symbionic.com.au" className="text-blue-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer" data-testid="symbionic-link-privacy">
                      Symbionic
                    </a>.
                  </p>
                </div>
                <picture>
                  <source srcSet="/whatsapp-qr.webp" type="image/webp" />
                  <img src="/whatsapp-qr-320.png" width="344" height="320" alt="WhatsApp QR code to message Bruce" className="w-28 h-28 rounded-lg border border-slate-200 bg-white p-1" data-testid="whatsapp-qr-privacy" />
                </picture>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="border-t border-slate-200 pt-6">
              <p className="text-sm text-slate-500 text-center">
                By using VerifAEye, you acknowledge that you have read and understood this Privacy Policy.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-4 py-8 text-center">
        <p className="text-sm text-slate-500">
          © 2026 VerifAEye. The Proof is in the Detail.
        </p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
